import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function BusinessStructureGuidePage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Business Structure Guide",
        "item": "https://elpasotexasbusiness.com/resources/business-structure-guide"
      }
    ]
  };

  const structures = [
    {
      title: 'Sole Proprietorship',
      icon: 'ri-user-line',
      pros: [
        'Easiest and least expensive to establish',
        'Complete control over business decisions',
        'Simple tax filing (Schedule C)',
        'Minimal paperwork and regulations',
      ],
      cons: [
        'Unlimited personal liability',
        'Difficult to raise capital',
        'Business ends if owner dies',
        'All business debts are personal debts',
      ],
      bestFor: 'Low-risk businesses, freelancers, and solo consultants',
    },
    {
      title: 'Limited Liability Company (LLC)',
      icon: 'ri-shield-check-line',
      pros: [
        'Personal liability protection',
        'Flexible tax options (pass-through or corporate)',
        'Less paperwork than corporations',
        'Credibility with customers and vendors',
      ],
      cons: [
        'More expensive than sole proprietorship',
        'Annual franchise tax in Texas',
        'Requires operating agreement',
        'Self-employment taxes on all profits',
      ],
      bestFor: 'Most small to medium businesses seeking liability protection',
    },
    {
      title: 'Corporation (C-Corp)',
      icon: 'ri-building-4-line',
      pros: [
        'Strongest personal liability protection',
        'Easier to raise capital through stock',
        'Unlimited life span',
        'Tax deductible business expenses',
      ],
      cons: [
        'Double taxation (corporate and personal)',
        'Extensive record-keeping requirements',
        'More expensive to establish and maintain',
        'Complex regulatory compliance',
      ],
      bestFor: 'Businesses planning to raise significant capital or go public',
    },
    {
      title: 'S Corporation',
      icon: 'ri-organization-chart',
      pros: [
        'Pass-through taxation (no double tax)',
        'Personal liability protection',
        'Potential tax savings on self-employment',
        'Enhanced credibility',
      ],
      cons: [
        'Strict ownership restrictions (max 100 shareholders)',
        'Must be U.S. citizens or residents',
        'Required corporate formalities',
        'IRS scrutiny on salary vs. distributions',
      ],
      bestFor: 'Profitable businesses wanting to minimize self-employment taxes',
    },
    {
      title: 'Partnership',
      icon: 'ri-team-line',
      pros: [
        'Easy to establish',
        'Shared financial commitment',
        'Pass-through taxation',
        'Combined expertise and resources',
      ],
      cons: [
        'Joint liability for business debts',
        'Potential for partner conflicts',
        'Shared profits',
        'Each partner can bind the business',
      ],
      bestFor: 'Two or more people starting a business together',
    },
  ];

  const texasSteps = [
    {
      step: '1',
      title: 'Choose Your Business Name',
      description: 'Ensure your name is available and complies with Texas naming requirements. Check availability through the Texas Secretary of State website.',
    },
    {
      step: '2',
      title: 'File Formation Documents',
      description: 'Submit Certificate of Formation (LLC) or Articles of Incorporation (Corporation) to the Texas Secretary of State. Filing fee: $300 for LLC, $300 for Corporation.',
    },
    {
      step: '3',
      title: 'Obtain EIN',
      description: 'Apply for an Employer Identification Number (EIN) from the IRS. This is free and can be done online immediately.',
    },
    {
      step: '4',
      title: 'Register with Texas Comptroller',
      description: 'Register for state taxes if applicable. Most businesses need to register for sales tax, franchise tax, or employment taxes.',
    },
    {
      step: '5',
      title: 'Create Operating Agreement',
      description: 'Draft an operating agreement (LLC) or bylaws (Corporation) to establish internal rules and ownership structure.',
    },
    {
      step: '6',
      title: 'Register in El Paso',
      description: 'Obtain local business permits and licenses from the City of El Paso. Requirements vary by business type and location.',
    },
  ];

  return (
    <>
      <SEOHead
        title="Business Structure Guide - LLC, Corporation, Sole Proprietorship in Texas"
        description="Choose the right business structure for your El Paso company. Compare LLC, Corporation, Partnership, and Sole Proprietorship options with Texas-specific legal and tax implications explained."
        canonical="https://elpasotexasbusiness.com/resources/business-structure-guide"
        ogImage="https://public.readdy.ai/ai/img_res/d3c57ef9bfac263557479137f69933e7.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold text-sm flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Business Structure Guide</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Choosing the right legal structure is one of the most important decisions you'll make for your El Paso business. This guide will help you understand your options and make an informed choice.
              </p>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Your Business Structure Matters</h2>
              <div className="bg-[#FAFAF8] rounded-2xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-shield-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Liability Protection</h3>
                    <p className="text-sm text-gray-600">Determines whether your personal assets are protected from business debts and lawsuits.</p>
                  </div>
                  <div>
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Tax Implications</h3>
                    <p className="text-sm text-gray-600">Affects how your business income is taxed and what deductions you can claim.</p>
                  </div>
                  <div>
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-funds-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Funding Options</h3>
                    <p className="text-sm text-gray-600">Influences your ability to raise capital from investors or financial institutions.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Compare Business Structures</h2>
              <div className="space-y-8">
                {structures.map((structure, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${structure.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{structure.title}</h3>
                        <p className="text-sm text-gray-600 italic">Best for: {structure.bestFor}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                          <i className="ri-checkbox-circle-line text-green-600"></i>
                          <span>Advantages</span>
                        </h4>
                        <ul className="space-y-2">
                          {structure.pros.map((pro, proIndex) => (
                            <li key={proIndex} className="text-sm text-gray-700 flex items-start space-x-2">
                              <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                          <i className="ri-close-circle-line text-red-600"></i>
                          <span>Disadvantages</span>
                        </h4>
                        <ul className="space-y-2">
                          {structure.cons.map((con, conIndex) => (
                            <li key={conIndex} className="text-sm text-gray-700 flex items-start space-x-2">
                              <i className="ri-close-line text-red-600 mt-0.5 flex-shrink-0"></i>
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Texas Filing Requirements</h2>
              <div className="space-y-6">
                {texasSteps.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 flex items-start space-x-6">
                    <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white">{item.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12">
              <div className="max-w-3xl mx-auto text-center">
                <h3 className="text-4xl font-bold text-white mb-4">El Paso Specific Considerations</h3>
                <div className="space-y-4 text-left">
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-white mb-2">Border Business Opportunities</h4>
                    <p className="text-sm text-white/80">El Paso's location on the U.S.-Mexico border creates unique opportunities for international trade. Consider structures that facilitate cross-border operations.</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-white mb-2">Local Incentives</h4>
                    <p className="text-sm text-white/80">The City of El Paso offers various incentives for new businesses. Your business structure may affect eligibility for certain programs.</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-white mb-2">Professional Advice</h4>
                    <p className="text-sm text-white/80">Consult with a local El Paso attorney or CPA before making your final decision. They can provide guidance specific to your situation and industry.</p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg mt-8 whitespace-nowrap"
                >
                  <span>Get Personalized Help</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}